import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const Stat = ({ label, value, testid }) => (
  <Card data-testid={testid}>
    <CardContent className="p-5">
      <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">{label}</div>
      <div className="text-3xl font-bold text-[#064E3B]">{value}</div>
    </CardContent>
  </Card>
);

export default function PartnerDashboard() {
  const { partner } = useOutletContext();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/api/partner/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const stats = data?.stats || {};
  const pctUsed = stats.media_quota_mb
    ? Math.min(100, Math.round((stats.media_used_mb / stats.media_quota_mb) * 100))
    : 0;

  return (
    <div className="max-w-6xl space-y-6" data-testid="partner-dashboard-root">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Welcome, {partner.contact_name.split(" ")[0]} 👋</h1>
          <p className="text-sm text-gray-600 mt-1">{partner.organization_name}</p>
        </div>
        <Link to="/partner/courses">
          <Button className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="partner-new-course-cta">
            Manage courses →
          </Button>
        </Link>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <Stat label="Total courses" value={stats.total_courses ?? 0} testid="partner-stat-total-courses" />
        <Stat label="Published" value={stats.published_courses ?? 0} testid="partner-stat-published" />
        <Stat label="Pending review" value={stats.pending_review ?? 0} testid="partner-stat-pending-review" />
        <Stat label="Cohorts" value={stats.total_cohorts ?? 0} testid="partner-stat-cohorts" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card data-testid="partner-quota-card">
          <CardHeader><CardTitle className="text-base">Media quota</CardTitle></CardHeader>
          <CardContent>
            <div className="text-sm mb-2">
              <b>{stats.media_used_mb ?? 0} MB</b> used of {stats.media_quota_mb ?? 500} MB
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-[#DC2626]" style={{ width: `${pctUsed}%` }} />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Selective upload only — no bulk imports. Request a higher quota via teachmeonlineca@gmail.com.
            </p>
          </CardContent>
        </Card>

        <Card data-testid="partner-profile-card">
          <CardHeader><CardTitle className="text-base">Partner profile</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div><b>Slug:</b> {partner.slug}</div>
            <div><b>Contact:</b> {partner.contact_email}</div>
            <div><b>Country:</b> {partner.country} {partner.province && `— ${partner.province}`}</div>
            <div><b>Revenue share (informational):</b> {partner.revenue_share_pct}% partner</div>
            <div><b>Accreditations:</b></div>
            <div className="flex flex-wrap gap-1">
              {(partner.accreditations || []).map((a) => (
                <Badge key={a} variant="secondary" className="text-xs">{a}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
